import {createSlice} from '@reduxjs/toolkit';
import {accountUserApi} from '../../../api/accountUser/api';
import {userApi} from '../../../api/user/api';
import {getErrorMessage} from '../../utils/requests/error';

export const userRegistrationSlice = createSlice({
    name: 'userRegistration',
    initialState: {
        registration: null,
        user: null,
        oauthTokens: null,
        isLoading: false,
        isFetchingUser: false,
        isConfirming: false,
        isVerifying: false,
        isActivating: false,
        isCompletingRegistration: false,
        error: null,
        fetchUserError: null,
        confirmError: null,
        verifyError: null,
        activateError: null,
        completeRegistrationError: null,
        isConfirmed: false,
        isVerified: false,
        isActivated: false
    },
    reducers: {
        setRegistration: (state, action) => {
            state.registration = action.payload;
        },
        setOAuthTokens: (state, action) => {
            state.oauthTokens = action.payload;
        },
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setIsConfirming: (state, action) => {
            state.isConfirming = action.payload;
        },
        setIsVerifying: (state, action) => {
            state.isVerifying = action.payload;
        },
        setIsActivating: (state, action) => {
            state.isActivating = action.payload;
        },
        setIsCompletingRegistration: (state, action) => {
            state.isCompletingRegistration = action.payload;
        },
        setUser: (state, action) => {
            state.user = action.payload;
        },
        setIsFetchingUser: (state, action) => {
            state.isFetchingUser = action.payload;
        },
        setFetchUserError: (state, action) => {
            state.fetchUserError = action.payload;
        },
        setCompleteRegistrationError: (state, action) => {
            state.completeRegistrationError = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setConfirmError: (state, action) => {
            state.confirmError = action.payload;
        },
        setVerifyError: (state, action) => {
            state.verifyError = action.payload;
        },
        setActivateError: (state, action) => {
            state.activateError = action.payload;
        },
        setIsConfirmed: (state, action) => {
            state.isConfirmed = action.payload;
        },
        setIsVerified: (state, action) => {
            state.isVerified = action.payload;
        },
        setIsActivated: (state, action) => {
            state.isActivated = action.payload;
        },
        resetRegistrationState: (state) => {
            state.registration = null;
            state.user = null;
            state.oauthTokens = null;
            state.isLoading = false;
            state.isFetchingUser = false;
            state.isConfirming = false;
            state.isVerifying = false;
            state.isActivating = false;
            state.isCompletingRegistration = false;
            state.error = null;
            state.fetchUserError = null;
            state.confirmError = null;
            state.verifyError = null;
            state.activateError = null;
            state.completeRegistrationError = null;
            state.isConfirmed = false;
            state.isVerified = false;
            state.isActivated = false;
        },
        clearErrors: (state) => {
            state.error = null;
            state.fetchUserError = null;
            state.confirmError = null;
            state.verifyError = null;
            state.activateError = null;
            state.completeRegistrationError = null;
        }
    }
});

export const {
    setRegistration,
    setOAuthTokens,
    setIsLoading,
    setIsConfirming,
    setIsVerifying,
    setIsActivating,
    setIsCompletingRegistration,
    setUser,
    setIsFetchingUser,
    setFetchUserError,
    setCompleteRegistrationError,
    setError,
    setConfirmError,
    setVerifyError,
    setActivateError,
    setIsConfirmed,
    setIsVerified,
    setIsActivated,
    resetRegistrationState,
    clearErrors
} = userRegistrationSlice.actions;

export const fetchRegistration = ({accountId, userId, registrationId}) => async (dispatch) => {
    dispatch(setIsLoading(true));
    dispatch(setError(null));

    try {
        const response = await accountUserApi.getRegistration({accountId, userId, registrationId, options: {useS2SAuth: true}});
        dispatch(setRegistration(response.data));
        dispatch(setIsLoading(false));
        return response.data;
    } catch (err) {
        dispatch(setIsLoading(false));
        dispatch(setError(getErrorMessage(err)));
        return false;
    }
};

export const confirmRegistration = ({accountId, userId, registrationId}) => async (dispatch) => {
    dispatch(setIsConfirming(true));
    dispatch(setConfirmError(null));

    try {
        await accountUserApi.confirmRegistration({accountId, userId, registrationId, options: {useS2SAuth: true}});
        dispatch(setIsConfirming(false));
        dispatch(setIsConfirmed(true));
        return true;
    } catch (err) {
        dispatch(setIsConfirming(false));
        dispatch(setConfirmError(getErrorMessage(err)));
        return false;
    }
};

export const verifyRegistration = ({accountId, userId, registrationId, secret}) => async (dispatch) => {
    dispatch(setIsVerifying(true));
    dispatch(setVerifyError(null));

    try {
        const response = await accountUserApi.verifyRegistration({
            accountId,
            userId,
            registrationId,
            data: {secret: parseInt(secret, 10)},
            options: {useS2SAuth: true}
        });
        const oauthTokens = response.data;
        dispatch(setOAuthTokens(oauthTokens));

        await dispatch(fetchUser({userId, accessToken: oauthTokens.access_token}));

        dispatch(setIsVerifying(false));
        dispatch(setIsVerified(true));
        return oauthTokens;
    } catch (err) {
        dispatch(setIsVerifying(false));
        dispatch(setVerifyError(getErrorMessage(err)));
        return false;
    }
};

export const activateUser = ({accountId, userId, registrationId, secret, data, accessToken}) => async (dispatch) => {
    dispatch(setIsActivating(true));
    dispatch(setActivateError(null));

    try {
        await userApi.activateUser({
            userId,
            data,
            options: accessToken ? {accessToken} : undefined
        });

        await accountUserApi.completeRegistration({
            accountId,
            userId,
            registrationId,
            data: {secret: parseInt(secret, 10)},
            options: {useS2SAuth: true}
        });

        dispatch(setIsActivating(false));
        dispatch(setIsActivated(true));
        return true;
    } catch (err) {
        dispatch(setIsActivating(false));
        dispatch(setActivateError(getErrorMessage(err)));
        return false;
    }
};

export const fetchUser = ({userId, accessToken}) => async (dispatch) => {
    dispatch(setIsFetchingUser(true));
    dispatch(setFetchUserError(null));

    try {
        const options = accessToken ? {accessToken} : undefined;
        const response = await userApi.getUser({userId, options});
        dispatch(setUser(response.data));
        dispatch(setIsFetchingUser(false));
        return response.data;
    } catch (err) {
        dispatch(setIsFetchingUser(false));
        dispatch(setFetchUserError(getErrorMessage(err)));
        return null;
    }
};

export const completeRegistrationForExistingUser = ({accountId, userId, registrationId, secret}) => async (dispatch) => {
    dispatch(setIsCompletingRegistration(true));
    dispatch(setCompleteRegistrationError(null));

    try {
        await accountUserApi.completeRegistration({
            accountId,
            userId,
            registrationId,
            data: {secret: parseInt(secret, 10)},
            options: {useS2SAuth: true}
        });

        dispatch(setIsCompletingRegistration(false));
        dispatch(setIsActivated(true));
        return true;
    } catch (err) {
        dispatch(setIsCompletingRegistration(false));
        dispatch(setCompleteRegistrationError(getErrorMessage(err)));
        return false;
    }
};

export const selectRegistration = (state) => state.userRegistration.registration;
export const selectOAuthTokens = (state) => state.userRegistration.oauthTokens;
export const selectIsLoading = (state) => state.userRegistration.isLoading;
export const selectIsConfirming = (state) => state.userRegistration.isConfirming;
export const selectIsVerifying = (state) => state.userRegistration.isVerifying;
export const selectIsActivating = (state) => state.userRegistration.isActivating;
export const selectError = (state) => state.userRegistration.error;
export const selectConfirmError = (state) => state.userRegistration.confirmError;
export const selectVerifyError = (state) => state.userRegistration.verifyError;
export const selectActivateError = (state) => state.userRegistration.activateError;
export const selectIsConfirmed = (state) => state.userRegistration.isConfirmed;
export const selectIsVerified = (state) => state.userRegistration.isVerified;
export const selectIsActivated = (state) => state.userRegistration.isActivated;
export const selectUser = (state) => state.userRegistration.user;
export const selectIsFetchingUser = (state) => state.userRegistration.isFetchingUser;
export const selectFetchUserError = (state) => state.userRegistration.fetchUserError;
export const selectIsCompletingRegistration = (state) => state.userRegistration.isCompletingRegistration;
export const selectCompleteRegistrationError = (state) => state.userRegistration.completeRegistrationError;

export const selectIsUserActivated = (state) => {
    const user = state.userRegistration.user;
    if (!user?.first_name?.trim() || !user?.last_name?.trim() || !user?.password_updated_at) {
        return false;
    }
    const passwordUpdatedAt = new Date(user.password_updated_at).getTime();
    const createdAt = new Date(user.created_at).getTime();
    return passwordUpdatedAt > createdAt;
};

export default userRegistrationSlice.reducer;
